/**
 * Individual navigation item component for the sidebar
 *
 * Renders a link with support for active state highlighting and hover/focus effects.
 * Uses Framer Motion for smooth transitions between states.
 *
 * @example
 * ```tsx
 * <SidebarItem
 *   href="/dashboard"
 *   current={true}
 *   focused={false}
 * >
 *   <DashboardIcon className="mr-2 h-5 w-5" />
 *   <Text>Dashboard</Text>
 * </SidebarItem>
 * ```
 */
import { motion } from 'motion/react';

import { cn } from '@core/utils/styles';

import { Link } from '../../Link';

const baseClasses = [
  'relative flex items-center gap-2 rounded-theme px-3 py-2',
  'data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary',
  'text-sm font-medium text-txt-light dark:text-txt-dark',
];

const activeClasses = [
  'bg-back-light dark:bg-back-dark',
  'inset-ring rounded-theme ring ring-primary-800/10 dark:ring-primary-700/20 dark:inset-ring-primary/20',
  'inset-shadow-sm shadow inset-shadow-primary/50 inset-ring-primary-700/10 dark:inset-shadow-primary-light/50',
];

const highlightClasses = ['bg-back-nav-highlight-light/5 dark:bg-back-nav-highlight-dark'];

/**
 * Props for the SidebarItem component
 * @interface SidebarItemProps
 * @extends {React.HTMLAttributes<HTMLDivElement>}
 */
interface SidebarItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** URL that the item links to */
  href: string;
  /** Whether this item represents the current active route */
  current?: boolean;
  /** Whether this item is currently focused or hovered */
  focused?: boolean;
}

export const SidebarItem = ({
  href,
  current,
  focused,
  children,
  className,
  ...props
}: SidebarItemProps) => {
  return (
    <div className={cn('relative', className)} {...props}>
      {focused && (
        <motion.span
          layoutId="sidebar-highlight"
          className={cn(highlightClasses, 'rounded-theme absolute inset-0 z-0')}
        />
      )}

      {current && (
        <motion.span
          layoutId="sidebar-current"
          className={cn(
            activeClasses,
            'absolute inset-y-0 -left-4.5 z-1 w-[calc(100%+1.1rem)] rounded-l-none'
          )}
        />
      )}

      <Link href={href} className={cn(baseClasses, 'z-1 h-full w-full')}>
        {children}
      </Link>
    </div>
  );
};
